import { isDefined, isEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { SpTooltip } from 'components/shared/Tooltip/Tooltip';
import { useSPIntl } from 'hooks/useIntl';
import type { FormError } from 'models/formError';
import { type FC, useMemo } from 'react';
import { translateValidationError } from 'utils/errors/form-validation-errors';

export type CountBadgeColor = 'warning' | 'info';

type TooltipContentProps = {
  validations: FormError[];
  errorPrefix?: string;
};

const TooltipContent: FC<TooltipContentProps> = ({ validations, errorPrefix }) => {
  const intl = useSPIntl();
  const messages = useMemo(
    () =>
      validations.map(v => translateValidationError(v, intl, errorPrefix ?? 'quote.errorMessages.')).filter(isDefined),
    [errorPrefix, intl, validations],
  );
  return (
    <>
      {messages.map(({ message }, idx) => (
        <div key={idx} className="d-flex align-items-center fs-6 my-1">
          <em className="icon icon-sm me-2">info_outline</em>
          <span className="text-start">{message}</span>
        </div>
      ))}
    </>
  );
};

type CountBadgeProps = {
  id: string;
  count: number;
  color: CountBadgeColor;
  validations?: FormError[];
  errorPrefix?: string;
};

/**
 * Presentational pill badge with a count and an optional validation tooltip. Renders nothing when the
 * count is 0. Used for the validation (orange) and info/modified (blue) field-count badges.
 */
export const CountBadge: FC<CountBadgeProps> = ({ id, count, color, validations = [], errorPrefix }) => {
  if (count === 0) {
    return undefined;
  }

  return (
    <SpTooltip
      disabled={isEmpty(validations)}
      color={color}
      position="right"
      contents={<TooltipContent validations={validations} errorPrefix={errorPrefix} />}
    >
      <div className="fs-6 ms-2 d-inline-block">
        <span
          id={id}
          className={classNames('badge rounded-pill', {
            'badge-warning': color === 'warning',
            'badge-info': color === 'info',
          })}
        >
          {count}
        </span>
      </div>
    </SpTooltip>
  );
};
